import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  User,
  Stock,
  Transaction,
  AuthUser,
  LoginFormData,
  RegisterFormData,
  Portfolio,
  WatchlistData,
  GoogleUser,
  TradingAccount,
} from "../types";
import { auth } from "../firebase";
import { AuthService } from "../services/authService";
import { StockService } from "../services/stockService";
import { TransactionService } from "../services/transactionService";
import { PortfolioService } from "../services/portfolioService";
import { WatchlistService } from "../services/watchlistService";
import { MultiAccountService } from "../services/multiAccountService";
import { HoldingsCalculationService } from "../services/holdingsCalculationService";
import ErrorHandler, { AppError } from "../utils/errorHandler";
import { isDelistedStock } from "../data/delistedStocks";
import { useStockData } from './StockDataContext';
import { logger } from '../utils/logger';

interface AppState {
  // Authentication state
  authUser: AuthUser | null;
  currentUser: User | null;
  authLoading: boolean;

  // Multi-account state
  googleUser: GoogleUser | null;
  availableAccounts: TradingAccount[];
  currentAccountId: string | null;
  currentAccount: TradingAccount | null;

  // App data
  stocks: Stock[];
  transactions: Transaction[];
  portfolio: Portfolio | null;
  watchlist: WatchlistData | null;

  // UI state
  isLoading: boolean;
  error: AppError | null;
  accessDenied: boolean;
}

type AppAction =
  | { type: "SET_AUTH_USER"; payload: AuthUser | null }
  | { type: "SET_CURRENT_USER"; payload: User | null }
  | { type: "SET_AUTH_LOADING"; payload: boolean }
  | { type: "SET_GOOGLE_USER"; payload: GoogleUser | null }
  | { type: "SET_AVAILABLE_ACCOUNTS"; payload: TradingAccount[] }
  | {
      type: "SET_CURRENT_ACCOUNT";
      payload: { accountId: string | null; account: TradingAccount | null };
    }
  | { type: "SET_ACCESS_DENIED"; payload: boolean }
  | { type: "SET_STOCKS"; payload: Stock[] }
  // ARCH-01:股票狀態僅經由以下兩個合併型 action 變更,取代全量覆寫
  | { type: "MERGE_STOCK_PRICES"; payload: Stock[] } // 以 payload 為準合併(即時現價)
  | { type: "ADD_MISSING_STOCKS"; payload: Stock[] } // 僅補齊缺少的 symbol,不覆蓋既有
  | { type: "SET_TRANSACTIONS"; payload: Transaction[] }
  | { type: "SET_PORTFOLIO"; payload: Portfolio | null }
  | { type: "SET_WATCHLIST"; payload: WatchlistData | null }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: AppError | null }
  | { type: "CLEAR_ERROR" };

const initialState: AppState = {
  authUser: null,
  currentUser: null,
  authLoading: true,
  googleUser: null,
  availableAccounts: [],
  currentAccountId: null,
  currentAccount: null,
  stocks: [],
  transactions: [],
  portfolio: null,
  watchlist: null,
  isLoading: false,
  error: null,
  accessDenied: false,
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case "SET_AUTH_USER":
      return { ...state, authUser: action.payload };
    case "SET_CURRENT_USER":
      return { ...state, currentUser: action.payload };
    case "SET_AUTH_LOADING":
      return { ...state, authLoading: action.payload };
    case "SET_GOOGLE_USER":
      return { ...state, googleUser: action.payload };
    case "SET_AVAILABLE_ACCOUNTS":
      return { ...state, availableAccounts: action.payload };
    case "SET_CURRENT_ACCOUNT":
      return {
        ...state,
        currentAccountId: action.payload.accountId,
        currentAccount: action.payload.account,
      };
    case "SET_ACCESS_DENIED":
      return { ...state, accessDenied: action.payload };
    case "SET_STOCKS":
      return { ...state, stocks: action.payload };
    case "MERGE_STOCK_PRICES": {
      // 以更新資料為準合併(保留未提及的既有股票)
      const map = new Map(state.stocks.map((s) => [s.symbol, s]));
      action.payload.forEach((s) => map.set(s.symbol, s));
      return { ...state, stocks: Array.from(map.values()) };
    }
    case "ADD_MISSING_STOCKS": {
      // 僅補齊缺少的 symbol,絕不覆蓋既有(既有可能帶有較新的現價)
      const map = new Map(state.stocks.map((s) => [s.symbol, s]));
      const before = map.size;
      action.payload.forEach((s) => {
        if (!map.has(s.symbol)) map.set(s.symbol, s);
      });
      const stocks = Array.from(map.values());
      // 內容比對(ARCH-07):沒有新增任何股票時回傳原 state,避免無意義的重渲染
      return stocks.length === before ? state : { ...state, stocks };
    }
    case "SET_TRANSACTIONS":
      return { ...state, transactions: action.payload };
    case "SET_PORTFOLIO":
      return { ...state, portfolio: action.payload };
    case "SET_WATCHLIST":
      return { ...state, watchlist: action.payload };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "CLEAR_ERROR":
      return { ...state, error: null };
    default:
      return state;
  }
};

interface AppContextType {
  // State
  state: AppState;

  // Authentication methods
  login: (formData: LoginFormData) => Promise<void>;
  register: (formData: RegisterFormData) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;

  // Multi-account methods
  switchAccount: (accountId: string) => Promise<void>;

  // Transaction methods
  addTransaction: (
    transactionData: Omit<Transaction, "id" | "accountId" | "timestamp">  // 更新為 accountId
  ) => Promise<void>;
  updateTransaction: (
    transactionId: string,
    updates: Partial<Omit<Transaction, "id" | "accountId">>
  ) => Promise<void>;
  deleteTransaction: (transactionId: string) => Promise<void>;

  // Data refresh methods
  refreshPortfolio: () => Promise<void>;
  refreshStocks: () => Promise<void>;

  // 語意化狀態更新方法(ARCH-06:dispatch 不再對外暴露,
  // 所有 state 變更一律經由 provider 方法,單一寫入路徑)
  mergeStockPrices: (updatedStocks: Stock[]) => void;
  updateWatchlistData: (watchlist: WatchlistData | null) => void;

  // Utility methods
  clearError: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // auth 事件版本號:防止快速登入/登出時,舊的非同步 callback 把過期狀態 dispatch 回來
  const authVersionRef = React.useRef(0);

  // 使用 StockDataContext（使用完整股票資料）
  const { fullStocks, refreshStocks: refreshStockData } = useStockData();
  
  // 同步 StockDataContext 的完整資料到 AppContext
  // ARCH-01:一律只「補齊缺少的」,不覆蓋已存在(可能帶有較新現價)的股票
  useEffect(() => {
    if (fullStocks.length > 0) {
      dispatch({ type: "ADD_MISSING_STOCKS", payload: fullStocks });
    }
  }, [fullStocks]);

  // Helper function to load user data
  const loadUserData = useCallback(async (userId: string) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      // Load transactions
      const transactions = await TransactionService.getUserTransactions(userId);
      dispatch({ type: "SET_TRANSACTIONS", payload: transactions });
      
      // 🔥 新增：將交易記錄中的股票名稱更新到 StockNameManager
      if (transactions.length > 0) {
        logger.context.debug('📝 開始更新交易記錄中的股票名稱');
        
        // 提取所有交易中的股票名稱資訊
        const transactionStockUpdates = transactions
          .filter(transaction => transaction.stockName) // 只處理有名稱的交易
          .map(transaction => ({
            symbol: transaction.symbol,
            sources: {
              transactionName: transaction.stockName,
              staticName: transaction.stockName
            }
          }));
        
        if (transactionStockUpdates.length > 0) {
          const { StockNameManager } = await import('../services/stockNameManager');
          const updateResult = StockNameManager.smartUpdateBatch(transactionStockUpdates);
          logger.context.info('📝 交易記錄股票名稱更新完成', {
            ...updateResult,
            processedTransactions: transactionStockUpdates.length
          });
        } else {
          logger.context.debug('ℹ️ 交易記錄中沒有股票名稱資訊');
        }
      }

      // Load portfolio
      const portfolio = await PortfolioService.getUserPortfolio(userId);
      dispatch({ type: "SET_PORTFOLIO", payload: portfolio });

      // Load watchlist
      const watchlist = await WatchlistService.getUserWatchlist(userId);
      dispatch({ type: "SET_WATCHLIST", payload: watchlist });
    } catch (error) {
      logger.context.error('Failed to load user data', error);
      const appError = ErrorHandler.handleError(error, "Load User Data");
      dispatch({ type: "SET_ERROR", payload: appError });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [dispatch]);

  // Helper function to load stocks with names from transactions
  // ARCH-01/ARCH-07:僅「補齊缺少的股票」,不再全量覆寫 state.stocks —
  // 舊邏輯會用快取資料蓋掉 Holdings 剛輪詢到的即時現價
  const loadStocks = useCallback(async (additionalSymbols: string[] = []) => {
    try {
      // 如果 StockDataContext 的資料為空，主動觸發載入
      const stocks = fullStocks;

      if (stocks.length === 0) {
        // 主動觸發 StockDataContext 的資料載入
        await refreshStockData();
        return; // 資料會通過 useEffect 同步
      }

      // 只查詢目前持有的股票，避免對已下市/過往紀錄的股票進行 API 查詢
      const accountTransactions = state.transactions.filter(t =>
        t.accountId === state.currentAccountId &&
        t.type !== 'cash_deposit' &&
        t.type !== 'cash_withdrawal'
      );
      const holdingsResult = HoldingsCalculationService.calculateHoldingsFromTransactions(
        accountTransactions,
        stocks,
        { includeFees: true, includeHistorical: false }
      );
      const currentlyHeldSymbols = holdingsResult.currentHoldings
        .filter(h => h.currentQuantity > 0 && !isDelistedStock(h.symbol))
        .map(h => h.symbol);

      const filteredAdditionalSymbols = additionalSymbols.filter(s => !isDelistedStock(s));
      const allSymbolsToCheck = new Set([
        ...currentlyHeldSymbols,
        ...filteredAdditionalSymbols
      ]);
      const stockLookup = new Map<string, Stock>();
      stocks.forEach(stock => stockLookup.set(stock.symbol, stock));

      // 找出 state 中缺少、快取也沒有的 symbol,平行補抓(UI-05:取代序列 await)
      const missingSymbols = Array.from(allSymbolsToCheck).filter(
        symbol => !stockLookup.has(symbol)
      );

      if (missingSymbols.length > 0) {
        const results = await Promise.allSettled(
          missingSymbols.map(symbol => StockService.getStockBySymbol(symbol))
        );
        results.forEach(result => {
          if (result.status === 'fulfilled' && result.value) {
            stockLookup.set(result.value.symbol, result.value);
          }
        });
      }

      // 僅補齊缺少的股票(reducer 內建內容比對,無新增時不觸發重渲染)
      dispatch({ type: "ADD_MISSING_STOCKS", payload: Array.from(stockLookup.values()) });
    } catch (error) {
      logger.context.error('Failed to load stocks in AppContext', error);
      const appError = ErrorHandler.handleError(error, "Load Stocks");
      dispatch({ type: "SET_ERROR", payload: appError });
    }
  }, [state.transactions, state.currentAccountId, fullStocks, refreshStockData]);

  // Helper function to load multi-account data
  const loadMultiAccountData = useCallback(
    async (userId: string, authUser: AuthUser | null) => {
      try {
        dispatch({ type: "SET_LOADING", payload: true });

        // Try to load GoogleUser document for this user
        let googleUser = await MultiAccountService.getGoogleUser(userId);

        // If no GoogleUser document exists, create one for the new user
        // 新使用者一律建立為 pending 狀態(role: 'user', isAllowed: false),
        // admin 授予須由已存在的 admin 於使用者管理頁面或 Firebase Console 進行
        if (!googleUser && authUser) {
          logger.context.info('Creating new Google user (requires admin approval)', { uid: authUser.uid });
          const newGoogleUser: GoogleUser = {
            uid: authUser.uid,
            email: authUser.email || '',
            displayName: authUser.displayName || 'User',
            photoURL: authUser.photoURL || undefined,
            role: 'user',
            isAllowed: false, // Requires admin approval
            allowedAccounts: [],
            createdAt: new Date(),
          };

          await MultiAccountService.createGoogleUser(newGoogleUser);
          googleUser = await MultiAccountService.getGoogleUser(userId);
          logger.context.info('New Google user created, waiting for admin approval');
        }

        dispatch({ type: "SET_GOOGLE_USER", payload: googleUser });

        if (googleUser) {
          // Check if user is allowed to access the system
          if (!googleUser.isAllowed) {
            logger.context.warn('User not approved by admin yet - access denied');
            dispatch({ type: "SET_ACCESS_DENIED", payload: true });
            return; // Stop processing here
          }

          logger.context.info('User is approved by admin - proceeding with account setup');
          
          // User has multi-account setup, load available accounts based on GoogleUser permissions
          logger.context.info('Loading trading accounts for GoogleUser', {
            uid: googleUser.uid,
            email: googleUser.email,
            role: googleUser.role,
            allowedAccountsCount: googleUser.allowedAccounts.length
          });
          
          const availableAccounts = await MultiAccountService.getUserTradingAccounts(googleUser.uid);
          logger.context.info('Available trading accounts loaded', { count: availableAccounts.length });
          
          dispatch({
            type: "SET_AVAILABLE_ACCOUNTS",
            payload: availableAccounts,
          });

        if (availableAccounts.length > 0) {
          // Set the current account to either the default or the first available account
          // 防禦性 fallback:defaultAccountId 可能指向已刪除/已撤銷的帳號
          // (find() 落空時退回第一個可用帳號,避免使用者卡在無帳戶狀態)
          const defaultAccountId = googleUser.defaultAccountId;
          const targetAccount =
            (defaultAccountId
              ? availableAccounts.find((acc) => acc.id === defaultAccountId)
              : undefined) ?? availableAccounts[0];

          if (targetAccount) {
            logger.context.info('Setting current trading account', {
              accountId: targetAccount.id,
              accountName: targetAccount.name,
              accountType: targetAccount.accountType
            });
            
            dispatch({
              type: "SET_CURRENT_ACCOUNT",
              payload: {
                accountId: targetAccount.id,
                account: targetAccount,
              },
            });

            // Load account-specific data using the TRADING ACCOUNT ID, not the login user ID
            try {
              await loadUserData(targetAccount.id);
            } catch (loadUserDataError) {
              // 記錄錯誤但讓應用繼續運作(即時監聽器稍後會補上資料)
              logger.context.error('loadUserData failed', { accountId: targetAccount.id, error: loadUserDataError });
            }
          } else {
            logger.context.warn('❌ 找不到目標交易帳戶', { 
              defaultAccountId: googleUser.defaultAccountId,
              availableAccountsCount: availableAccounts.length,
              availableAccounts: availableAccounts.map(acc => ({ id: acc.id, name: acc.name }))
            });
          }
        } else {
          // Check if user is admin - admins can access even without trading accounts
          const isAdmin = googleUser.role === 'admin';
          if (isAdmin) {
            logger.context.info('Admin user detected - allowing access without trading accounts');
            // Admin can access the system for management purposes, but has no trading data
            // Don't load any trading data, just allow system access
            dispatch({ type: "SET_ACCESS_DENIED", payload: false });
          } else {
            // Regular user with no accessible accounts - deny access
            logger.context.warn('Regular user has no accessible trading accounts - access denied');
            dispatch({ type: "SET_ACCESS_DENIED", payload: true });
          }
        }
        } else {
          // GoogleUser 文件建立失敗 — 不得 fallback 到 uid 單人模式
          // (資料路徑與多帳戶不一致,會產生孤兒資料)
          logger.context.error('GoogleUser document could not be loaded or created');
          const appError = ErrorHandler.handleError(
            new Error('無法初始化使用者資料,請重新登入或聯絡管理員'),
            "Initialize User"
          );
          dispatch({ type: "SET_ERROR", payload: appError });
          dispatch({ type: "SET_ACCESS_DENIED", payload: true });
        }
      } catch (error) {
        // 載入失敗時顯示錯誤,不得靜默 fallback 到 uid 路徑載入資料
        logger.context.error('Load multi-account data error', error);
        const appError = ErrorHandler.handleError(error, "Load User Data");
        dispatch({ type: "SET_ERROR", payload: appError });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  // Firebase auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // 每次事件遞增版本號;非同步續行前檢查是否已被較新的事件取代
      const myVersion = ++authVersionRef.current;
      const isStale = () => authVersionRef.current !== myVersion;

      dispatch({ type: "SET_AUTH_LOADING", payload: true });

      try {
        if (firebaseUser) {
          // User is signed in
          const authUser = AuthService.mapFirebaseUser(firebaseUser);
          dispatch({ type: "SET_AUTH_USER", payload: authUser });

          // Get user profile from Firestore
          const userProfile = await AuthService.getUserProfile(firebaseUser.uid);
          if (isStale()) return;

          if (userProfile) {
            dispatch({ type: "SET_CURRENT_USER", payload: userProfile });
          } else {
            // If no user profile exists, create a basic one and continue
            const basicUser: User = {
              id: firebaseUser.uid,
              name: firebaseUser.displayName || 'User',
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || 'User',
              photoURL: firebaseUser.photoURL || '',
              createdAt: new Date(),
              lastLoginAt: new Date(),
            };
            dispatch({ type: "SET_CURRENT_USER", payload: basicUser });
          }

          // Initialize multi-account data
          await loadMultiAccountData(firebaseUser.uid, authUser);
          if (isStale()) return;
        } else {
          // User is signed out
          dispatch({ type: "SET_AUTH_USER", payload: null });
          dispatch({ type: "SET_CURRENT_USER", payload: null });
          dispatch({ type: "SET_GOOGLE_USER", payload: null });
          dispatch({ type: "SET_AVAILABLE_ACCOUNTS", payload: [] });
          dispatch({
            type: "SET_CURRENT_ACCOUNT",
            payload: { accountId: null, account: null },
          });
          dispatch({ type: "SET_ACCESS_DENIED", payload: false });
          dispatch({ type: "SET_TRANSACTIONS", payload: [] });
          dispatch({ type: "SET_PORTFOLIO", payload: null });
          dispatch({ type: "SET_WATCHLIST", payload: null });
        }
      } catch (error) {
        if (isStale()) return;
        logger.context.error('Auth state change error', error);
        const appError = ErrorHandler.handleError(error, "Authentication");
        dispatch({ type: "SET_ERROR", payload: appError });
      } finally {
        // 只有最新一次事件才能把 authLoading 設回 false
        if (!isStale()) {
          dispatch({ type: "SET_AUTH_LOADING", payload: false });
        }
      }
    });

    return () => {
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 注意:effect 內呼叫的 loadMultiAccountData 是 mount 當下的閉包版本。
  // 它能安全運作是因為其 useCallback 依賴僅 [dispatch](穩定);
  // 若未來在 loadMultiAccountData 加入不穩定的依賴,此監聽器會讀到過期閉包,
  // 屆時需改用 ref 存取最新版本。

  // ARCH-08:訂閱自身 googleUsers 文件 — 管理員核准後即時生效,不需重新登入
  useEffect(() => {
    const uid = state.authUser?.uid;
    if (!uid) return;

    const unsubscribe = MultiAccountService.subscribeToGoogleUser(uid, (googleUser) => {
      if (googleUser) {
        dispatch({ type: "SET_GOOGLE_USER", payload: googleUser });
      }
    });
    return unsubscribe;
  }, [state.authUser?.uid]);

  // Load stocks data on app initialization
  useEffect(() => {
    loadStocks();
  }, [loadStocks]);
  
  // Update stocks when transactions change
  useEffect(() => {
    if (state.transactions.length > 0) {
      // 只传递当前持仓的股票符号，避免查询已下市/已清仓的股票
      const uniqueSymbols = Array.from(new Set(state.transactions.map(t => t.symbol)));
      const currentHoldingSymbols = uniqueSymbols.filter(
        symbol => !isDelistedStock(symbol)
      );
      loadStocks(currentHoldingSymbols);
    }
  }, [state.transactions, loadStocks]);

  // Set up real-time listeners for the current trading account
  useEffect(() => {
    if (!state.authUser) return;
    
    // Only set up listeners if we have a current trading account
    // Admins without trading accounts shouldn't have trading data listeners
    if (!state.currentAccountId) {
      logger.context.info('No current account ID - skipping trading data listeners');
      return;
    }

    const tradingAccountId = state.currentAccountId;
    logger.context.info('Setting up real-time listeners for trading account', { accountId: tradingAccountId });
    
    const unsubscribeFunctions: (() => void)[] = [];

    // Listen to transactions for the current trading account
    logger.context.debug('設定交易記錄監聽器', { accountId: tradingAccountId });
    const unsubscribeTransactions =
      TransactionService.subscribeToUserTransactions(tradingAccountId, (transactions) => {
        logger.context.debug('收到即時交易記錄更新', { 
          accountId: tradingAccountId, 
          transactionsCount: transactions.length,
          sampleTransactions: transactions.slice(0, 3)
        });
        dispatch({ type: "SET_TRANSACTIONS", payload: transactions });
      });
    unsubscribeFunctions.push(unsubscribeTransactions);

    // Listen to portfolio for the current trading account
    const unsubscribePortfolio = PortfolioService.subscribeToPortfolio(
      tradingAccountId,
      (portfolio) => {
        logger.context.info('Received portfolio update for account', { accountId: tradingAccountId });
        dispatch({ type: "SET_PORTFOLIO", payload: portfolio });
      }
    );
    unsubscribeFunctions.push(unsubscribePortfolio);

    // Listen to watchlist for the current trading account
    const unsubscribeWatchlist = WatchlistService.subscribeToWatchlist(
      tradingAccountId,
      (watchlist) => {
        logger.context.info('Received watchlist update for account', { accountId: tradingAccountId });
        dispatch({ type: "SET_WATCHLIST", payload: watchlist });
      }
    );
    unsubscribeFunctions.push(unsubscribeWatchlist);

    // Listen to account balance changes
    const unsubscribeAccount = MultiAccountService.subscribeToAccountBalance(
      tradingAccountId,
      (account) => {
        if (account) {
          logger.context.info('Received account balance update', { 
            accountId: tradingAccountId,
            balance: account.balance 
          });
          dispatch({
            type: "SET_CURRENT_ACCOUNT",
            payload: {
              accountId: tradingAccountId,
              account: account,
            },
          });
        }
      }
    );
    unsubscribeFunctions.push(unsubscribeAccount);

    // Cleanup function
    return () => {
      logger.context.info('Cleaning up trading data listeners for account', { accountId: tradingAccountId });
      unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
    };
  }, [state.authUser, state.currentAccountId]);

  // Authentication methods
  const login = useCallback(async (formData: LoginFormData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "CLEAR_ERROR" });

      await AuthService.login(formData);
      // Auth state change will be handled by the listener
    } catch (error) {
      const appError = ErrorHandler.handleError(error, "Login");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  const register = useCallback(async (formData: RegisterFormData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "CLEAR_ERROR" });

      await AuthService.register(formData);
      // Auth state change will be handled by the listener
    } catch (error) {
      const appError = ErrorHandler.handleError(error, "Register");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "CLEAR_ERROR" });

      await AuthService.loginWithGoogle();
      // Auth state change will be handled by the listener
    } catch (error) {
      const appError = ErrorHandler.handleError(error, "Google Login");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "CLEAR_ERROR" });

      await AuthService.logout();
      // Auth state change will be handled by the listener
    } catch (error) {
      const appError = ErrorHandler.handleError(error, "Logout");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  // Transaction methods
  const addTransaction = useCallback(async (
    transactionData: Omit<Transaction, "id" | "accountId" | "timestamp">  // 更新為 accountId
  ) => {
    if (!state.authUser) {
      const error = new Error("User not authenticated");
      const appError = ErrorHandler.handleError(error, "Add Transaction");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    }

    // 交易必須歸屬於明確的交易帳戶;不得 fallback 到登入 uid(會產生孤兒資料)
    if (!state.currentAccountId) {
      const error = new Error("尚未選擇交易帳戶,無法新增交易");
      const appError = ErrorHandler.handleError(error, "Add Transaction");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    }

    const targetUserId = state.currentAccountId;

    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "CLEAR_ERROR" });

      await TransactionService.addTransaction(targetUserId, transactionData);
      // Transactions will be updated via real-time listener

      // Recalculate portfolio
      await PortfolioService.recalculatePortfolio(targetUserId);
    } catch (error) {
      const appError = ErrorHandler.handleError(error, "Add Transaction");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [state.authUser, state.currentAccountId, dispatch]);

  const updateTransaction = useCallback(async (
    transactionId: string,
    updates: Partial<Omit<Transaction, "id" | "accountId">>
  ) => {
    if (!state.authUser) {
      const error = new Error("User not authenticated");
      const appError = ErrorHandler.handleError(error, "Update Transaction");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    }

    // 交易必須歸屬於明確的交易帳戶;不得 fallback 到登入 uid
    if (!state.currentAccountId) {
      const error = new Error("尚未選擇交易帳戶,無法更新交易");
      const appError = ErrorHandler.handleError(error, "Update Transaction");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    }

    const targetUserId = state.currentAccountId;

    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "CLEAR_ERROR" });

      // Update transaction with timestamp
      const transactionUpdates = {
        ...updates,
        updatedAt: new Date(),
      };

      await TransactionService.updateTransaction(transactionId, transactionUpdates);
      // Transactions will be updated via real-time listener

      // Recalculate portfolio and account balance after update
      await PortfolioService.recalculatePortfolio(targetUserId);
      
      // Re-sync account balance based on all transactions
      await TransactionService.updateAccountBalanceAfterBatch(targetUserId);
    } catch (error) {
      const appError = ErrorHandler.handleError(error, "Update Transaction");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [state.authUser, state.currentAccountId, dispatch]);

  const deleteTransaction = useCallback(async (transactionId: string) => {
    if (!state.authUser) {
      const error = new Error("User not authenticated");
      const appError = ErrorHandler.handleError(error, "Delete Transaction");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    }

    // 重算目標帳戶以「被刪交易實際歸屬的帳戶」為準(FIN-12):
    // 刪除非目前選中帳戶的交易時,才不會重算錯帳戶導致餘額永久偏差
    const deletedTransaction = state.transactions.find(t => t.id === transactionId);
    const targetUserId = deletedTransaction?.accountId || state.currentAccountId;
    if (!targetUserId) {
      const error = new Error("無法判定交易歸屬帳戶,無法刪除");
      const appError = ErrorHandler.handleError(error, "Delete Transaction");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    }

    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "CLEAR_ERROR" });

      await TransactionService.deleteTransaction(transactionId);
      // Transactions will be updated via real-time listener

      // Recalculate portfolio and account balance after deletion
      await PortfolioService.recalculatePortfolio(targetUserId);
      
      // Re-sync account balance based on remaining transactions
      await TransactionService.updateAccountBalanceAfterBatch(targetUserId);
    } catch (error) {
      const appError = ErrorHandler.handleError(error, "Delete Transaction");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [state.authUser, state.transactions, state.currentAccountId, dispatch]);

  // Data refresh methods
  const refreshPortfolio = useCallback(async () => {
    if (!state.authUser) return;
    if (!state.currentAccountId) return;

    const targetUserId = state.currentAccountId;

    try {
      dispatch({ type: "SET_LOADING", payload: true });

      const portfolio = await PortfolioService.recalculatePortfolio(
        targetUserId
      );
      dispatch({ type: "SET_PORTFOLIO", payload: portfolio });
    } catch (error) {
      const appError = ErrorHandler.handleError(error, "Refresh Portfolio");
      dispatch({ type: "SET_ERROR", payload: appError });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [state.authUser, state.currentAccountId, dispatch]);

  const refreshStocks = useCallback(async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      
      // 使用 StockDataContext 的刷新功能
      await refreshStockData();
      
      // 同步已經在 useEffect 中處理，這裡不需要手動dispatch
    } catch (error) {
      const appError = ErrorHandler.handleError(error, "Refresh Stocks");
      dispatch({ type: "SET_ERROR", payload: appError });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [refreshStockData, dispatch]);

  // Utility methods
  const clearError = useCallback(() => {
    dispatch({ type: "CLEAR_ERROR" });
  }, [dispatch]);

  // ARCH-06:語意化狀態更新方法 — 元件不得直接 dispatch(單一寫入路徑)
  /** 合併即時現價(ARCH-01:取代 Holdings 直接 dispatch SET_STOCKS) */
  const mergeStockPrices = useCallback((updatedStocks: Stock[]) => {
    if (updatedStocks.length === 0) return;
    dispatch({ type: "MERGE_STOCK_PRICES", payload: updatedStocks });
  }, []);

  /** 更新本地 watchlist 狀態(服務寫入成功後同步 UI) */
  const updateWatchlistData = useCallback((watchlist: WatchlistData | null) => {
    dispatch({ type: "SET_WATCHLIST", payload: watchlist });
  }, []);


  // Multi-account methods
  const switchAccount = useCallback(async (accountId: string) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      const account = state.availableAccounts.find(
        (acc) => acc.id === accountId
      );
      if (!account) {
        throw new Error("Account not found");
      }

      dispatch({
        type: "SET_CURRENT_ACCOUNT",
        payload: { accountId, account },
      });

      // Set as default account
      if (state.googleUser) {
        await MultiAccountService.setDefaultAccount(
          state.googleUser.uid,
          accountId
        );
      }

      // Clear current data and reload for new account
      dispatch({ type: "SET_TRANSACTIONS", payload: [] });
      dispatch({ type: "SET_PORTFOLIO", payload: null });
      dispatch({ type: "SET_WATCHLIST", payload: null });

      // Load data for the new account
      if (state.authUser) {
        await loadUserData(accountId); // Use accountId instead of userId for account-specific data
      }
    } catch (error) {
      const appError = ErrorHandler.handleError(error, "Switch Account");
      dispatch({ type: "SET_ERROR", payload: appError });
      throw appError;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [state.availableAccounts, state.googleUser, state.authUser, loadUserData, dispatch]);

  // ARCH-05(過渡措施):value 以 useMemo 提供,避免 provider 內任何 state 變動
  // 就重建 value 造成所有消費者重渲染;完整拆分(Auth/Account/Market)另案進行
  const value: AppContextType = React.useMemo(() => ({
    state,
    login,
    register,
    loginWithGoogle,
    logout,
    switchAccount,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    refreshPortfolio,
    refreshStocks,
    mergeStockPrices,
    updateWatchlistData,
    clearError,
  }), [
    state, login, register, loginWithGoogle, logout, switchAccount,
    addTransaction, updateTransaction, deleteTransaction, refreshPortfolio,
    refreshStocks, mergeStockPrices, updateWatchlistData, clearError,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
